import {
  createContext,
  useContext,
  useState,
  useEffect,
  useCallback,
  type ReactNode,
} from "react";
import { api } from "../lib/api";
import type { AuthResponse } from "@gollegiella/shared";
import { useGameStore } from "../store/gameStore";

// ─── Types ────────────────────────────────────────────────────────────────────

export interface AuthUser {
  userId: string;
  username: string;
  avatarId: string;
}

interface AuthContextValue {
  user: AuthUser | null;
  token: string | null;
  isLoading: boolean;
  register: (data: RegisterData) => Promise<string | null>; // returns error string or null
  login: (email: string, password: string) => Promise<string | null>;
  logout: () => void;
}

export interface RegisterData {
  username: string;
  email: string;
  password: string;
  avatarId: string;
}

const TOKEN_KEY = "gollegiella_token";

// ─── JWT decode (client-side, no verification) ─────────────────────────────────

function decodeToken(token: string): AuthUser | null {
  try {
    const payload = JSON.parse(atob(token.split(".")[1])) as {
      sub: string;
      username: string;
      avatarId: string;
      exp: number;
    };
    if (payload.exp * 1000 < Date.now()) return null;
    return { userId: payload.sub, username: payload.username, avatarId: payload.avatarId };
  } catch {
    return null;
  }
}

// ─── Context ──────────────────────────────────────────────────────────────────

const AuthContext = createContext<AuthContextValue | null>(null);

export function AuthProvider({ children }: { children: ReactNode }) {
  const [token, setToken] = useState<string | null>(null);
  const [user, setUser] = useState<AuthUser | null>(null);
  const [isLoading, setIsLoading] = useState(true);

  // Rehydrate from localStorage on mount
  useEffect(() => {
    const stored = localStorage.getItem(TOKEN_KEY);
    if (stored) {
      const decoded = decodeToken(stored);
      if (decoded) {
        setToken(stored);
        setUser(decoded);
      } else {
        localStorage.removeItem(TOKEN_KEY);
      }
    }
    setIsLoading(false);
  }, []);

  function persist(newToken: string, gold?: number) {
    localStorage.setItem(TOKEN_KEY, newToken);
    setToken(newToken);
    setUser(decodeToken(newToken));
    // Sync authoritative gold from server into the game store
    if (gold !== undefined) {
      useGameStore.getState().setGold(gold);
    }
  }

  const register = useCallback(async (data: RegisterData): Promise<string | null> => {
    const result = await api.post<AuthResponse>("/auth/register", data);
    if (!result.ok) return result.error;
    persist(result.data.token, result.data.user.gold);
    return null;
  }, []);

  const login = useCallback(async (email: string, password: string): Promise<string | null> => {
    const result = await api.post<AuthResponse>("/auth/login", { email, password });
    if (!result.ok) return result.error;
    persist(result.data.token, result.data.user.gold);
    return null;
  }, []);

  const logout = useCallback(() => {
    localStorage.removeItem(TOKEN_KEY);
    setToken(null);
    setUser(null);
  }, []);

  return (
    <AuthContext.Provider value={{ user, token, isLoading, register, login, logout }}>
      {children}
    </AuthContext.Provider>
  );
}

export function useAuth(): AuthContextValue {
  const ctx = useContext(AuthContext);
  if (!ctx) throw new Error("useAuth must be used within <AuthProvider>");
  return ctx;
}
